import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormGroup, FormBuilder, Validators, AbstractControl } from "@angular/forms";
import { TitleCasePipe } from "@angular/common";
import { Router } from "@angular/router";
import { Subscription, BehaviorSubject, Observable, of, forkJoin, combineLatest, from } from "rxjs";
import { finalize, tap, catchError, switchMap, map } from "rxjs/operators";
import { SubHeaderService } from "../../../core/services/sub-header.service";
import { SubHeader } from "../../../core/models/sub-header.model";
import { WellService } from "../../../common/services/well.service";
import { ReservedHoleNumbers, County, ApiComponents, ReserveAPIRequest, Well, Lease, WellLocation, GISPoint, GISPointTypes, DirectionIdentifier, Coordinates, WellStatus, WellType, OrphanWellRequest } from "../../../common/models/well.model";
import { Note } from "../../../common/models/note.model";
import { DynamicDialogService } from "../../../core/services/dynamic-dialog.service";
import { Dialog, DialogButton, DialogOptionSet } from "../../../core/models/dialog.model";
import { LoaderService } from "../../../common/services/loader.service";
import { NoteService } from "../../../common/services/note.service";
import { isInArray } from "../../../common/models/form-validators";
import { OrganizationService } from "../../../common/services/organization.service";
import { OrganizationStub } from "../../../common/models/organization.model";
import { DocumentManagementService } from "../../../common/services/document-management.service";
import { OnBaseMappedKeyword, OnBaseKeywordMappingSearchSpec } from '../../../common/models/onBaseIntegrationAndMapping.model';
import { WellDirection } from "../../../form-controls/components/new-casing-plan/models/casing-plan.model";
import { Option } from "../../../common/models/flow.dto";
import { DomainDefinitionService } from "../../../builder/services/domain-definition.service";
import { Domain } from "../../../builder/models/domain.model";
import { DomainValue } from '../../../models/domain.model';
import { GisQueryService } from "../../../gis/services/gis-query.service";
import { GisTokenService } from "../../../gis/services/gis-token.service";
import { UpdatedGISValue } from "../../../models/request.result";

@Component({
    selector: 'app-reserve-api',
    templateUrl: './reserve-api.component.html',
    styleUrls: ['./reserve-api.component.scss'],
    providers: [GisQueryService, GisTokenService],
    standalone: false
})
export class ReserveApiComponent implements OnInit, OnDestroy {

	private _typeDescriptionOptions: DialogOptionSet[] = [];

    readonly currentHoleMinimum: number = 20000;
    readonly currentHoleMaximum: number = 59999;
    readonly historicHoleMinimum: number = 60000;
    readonly historicHoleMaximum: number = 89999;

    apiList: ReservedHoleNumbers[] = [];
    availableOwnerNumbers: string[] = [];
    availableOrganizations: OrganizationStub[] = [];

    get county(): County {
        if (this.form == null || this.form.get('county') == null) return null;
        let value = this.form.get('county').value;
        if (value == null || value == '') return null;
        return <County>value;
    }

    data: Observable<ReservedHoleNumbers[]> = of([]);
    dataLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

    dialogMatchText: string = "You have elected to generate API #[XXX].  Is this correct?";
    dialogMismatchText: string = "You have elected to generate API #[XXX].  The next available API# for this county in RICS is #[YYY].  Please confirm which API you wish to generate.";
    dialogDecline: Dialog = new Dialog("Please check RBDMS for the correct Hole Number and start over.", [new DialogButton("OK", false, () => { return; })]);
    dialogOrphanDecline: Dialog = new Dialog("Only Historic or Orphan wells can be assigned a 6-Series API#.", [new DialogButton("OK", false, () => { return; })]);
    dialogConfirmMatch: Dialog = new Dialog("", [new DialogButton("Yes", true, () => { this.generateProvided(); }), new DialogButton("No", false, () => { this.cancelGenerate(); })]);
    dialogConfirmMismatch: Dialog = new Dialog("", [new DialogButton("Provided API#", true, () => { this.generateProvided(); }), new DialogButton("RICS Recommended API", true, () => { this.generateRecommended(); }), new DialogButton("Cancel", false, () => { this.cancelGenerate(); })]);
	dialogNoSelection: Dialog = new Dialog("Please provide an organization name, RICS Organization Id or RBDMS Owner Number before searching.", [new DialogButton("OK", false, () => { return false; })]);
	dialogOrphanConfirmMatch: Dialog = new Dialog("", [new DialogButton("Yes", true, () => { this.startOrphanProvided(); }), new DialogButton("No", false, () => { this.cancelGenerate(); })]);
    dialogOrphanConfirmMismatch: Dialog = new Dialog("", [new DialogButton("Provided API#", true, () => { this.startOrphanProvided(); }), new DialogButton("RICS Recommended API", true, () => { this.startOrphanRecommended(); }), new DialogButton("Cancel", false, () => { this.cancelGenerate(); })]);
    dialogNoGIS: Dialog = new Dialog("GIS Services are unavailable and data could not be confirmed.", [new DialogButton("OK", false, () => { return false; })]);
    countyErrorMessage: string = "You have selected [COUNTY] County, but GIS places your coordinates in [GISCOUNTY] County.  Please correct the surface latitude and longitude, or return to the previous page and generate a new hole number from the correct county.";
    dialogCountyError: Dialog = new Dialog("", [new DialogButton("OK", false, () => { return false; })]);


    directionOptions: Option[] = [
        { id: "", text: "--Select--" } as Option,
        { id: WellDirection.Vertical, text: WellDirection.Vertical } as Option,
        { id: WellDirection.Directional, text: WellDirection.Directional } as Option,
        { id: WellDirection.Horizontal, text: WellDirection.Horizontal } as Option,
        { id: WellDirection.OtherHorizontal, text: WellDirection.OtherHorizontal } as Option
    ];

	form: FormGroup;

	get disableValidate(): boolean {
		if (this.form == null) return true;

	}

	get disableGenerate(): boolean {
		return this.formInvalid || this.county == null || !this.holeNumberInValidRange || !this.holeNumberAvailable;
	}


    get formattedAPI(): string {
        if (this.county == null || this.holeNumber == null) return "";
        const countyNumber = this.county.apiNumber.toString().padStart(3, "0");
        return `34${countyNumber}${this.holeNumber}0000`;
    }

    get formInvalid(): boolean {
        if (this.form == null) return false;
        return this.form.invalid;
	}

	get foundOrganizationName(): string {
		if (this.form == null) return "";
		let ctrl = this.form.get('foundOrganizationName');
		if (ctrl == null) return "";
		return ctrl.value;
	}

	set foundOrganizationName(value: string) {
		if (this.form == null) return;
		let ctrl = this.form.get('foundOrganizationName');
		if (ctrl == null) return;
		ctrl.setValue(value);
	}

	get foundRicsOrganizationId(): string {
		if (this.form == null) return "";
		let ctrl = this.form.get('foundRicsOrganizationId');
		if (ctrl == null) return "";
		return ctrl.value;
	}

	set foundRicsOrganizationId(value: string) {
		if (this.form == null) return;
		let ctrl = this.form.get('foundRicsOrganizationId');
		if (ctrl == null) return;
		ctrl.setValue(value);
	}

	get foundRbdmsOwnerNumber(): string {
		if (this.form == null) return "";
		let ctrl = this.form.get('foundRbdmsOwnerNumber');
		if (ctrl == null) return "";
		return ctrl.value;
	}

	set foundRbdmsOwnerNumber(value: string) {
		if (this.form == null) return;
		let ctrl = this.form.get('foundRbdmsOwnerNumber');
		if (ctrl == null) return;
		ctrl.setValue(value);
	}

    get holeNumber(): number | null {
        if (this.form == null || this.form.get('holeNumber') == null) return null;
        let value = this.form.get('holeNumber').value;
        if (value == null || value == '') return null;
        return <number>value;
    }

    get holeNumberAvailable(): boolean | null {
        if (this.holeNumber == null || this.holeNumber.toString().length != 5 || this.county == null) return null;
        let api = this.apiList.find(a => a.county.apiNumber === this.county.apiNumber);
        if (api == null) return null;
        return api.reserved.find(a => a === this.holeNumber) == null;
    }

    get holeNumberInValidRange(): boolean | null {
        if (this.isUIC == null || this.holeNumber == null) return null;
        return (!this.isUIC && this.holeNumber >= this.historicHoleMinimum && this.holeNumber <= this.historicHoleMaximum) ||
            (this.isUIC && this.holeNumber >= this.currentHoleMinimum && this.holeNumber <= this.currentHoleMaximum);
    }

    get inputForm(): FormGroup | null {
        if (this.form == null || this.form.get('inputForm') == null || this.form.get('inputForm').value == null) return null;
        return <FormGroup>this.form.get('inputForm').value;
    }

    get isConfirming(): boolean {
        if (this.form == null || this.form.get('confirmData') == null) return false;
        return <boolean>this.form.get('confirmData').value;
    }

    get isDirectional(): boolean {
        if (this.form == null || this.form.get('inputForm') == null || this.form.get('inputForm').get('slant') == null) return false;
        let direction = this.form.get('inputForm').get('slant').value;
        return direction === WellDirection.Directional;
    }

    get isHistoric(): boolean | null {
		if (this.form == null || this.form.get('historic') == null || this.form.get('historic').value == null) return null;
        return this.form.get('historic').value == 'yes';
    }

    get isHorizontal(): boolean {
        if (this.form == null || this.form.get('inputForm') == null || this.form.get('inputForm').get('slant') == null) return false;
        let direction = this.form.get('inputForm').get('slant').value;
        return direction === WellDirection.Horizontal || direction === WellDirection.OtherHorizontal;
    }

    get isInputting(): boolean {
        if (this.form == null || this.form.get('inputData') == null) return false;
        let testForm = this.form.get('inputData');
        return <boolean>testForm.value;
    }

    get isLoading(): boolean {
        return this.dataLoading.value;
    }

    get isUIC(): boolean | null {
        if (this.form == null || this.form.get("UIC") == null || this.form.get("UIC").value == null || this.form.get("UIC").value == "") return null;
        return this.form.get("UIC").value == 'yes';
    }

    get nextAPIForCounty(): string {
        if (this.county == null || this.isUIC == null) return null;
        let apiReservations = this.apiList.find(a => a.county.apiNumber == this.county.apiNumber);
        if (apiReservations == null) return null;
        let nextHole = (this.isUIC) ? apiReservations.nextAvailableCurrentHole : apiReservations.nextAvailableHistoricHole;
        const countyNumber = this.county.apiNumber.toString().padStart(3, "0");
        return `34${countyNumber}${nextHole}0000`;
    }

    get notes(): Note[] {
        if (this.form == null || this.form.get('inputForm') == null || this.form.get('inputForm').get('notes') == null) return [];
        if (this.form.get('inputForm').get('notes') == null) this.notes = [];
        return <Note[]>this.form.get('inputForm').get('notes').value;
    }

    set notes(value: Note[]) {
        if (this.form == null || this.form.get('inputForm') == null || this.form.get('inputForm').get('notes') == null) return;
        this.form.get('inputForm').get('notes').setValue(value);
	}

	get organizationNotFound(): boolean {
		if (this.form == null) return false;
		let value = (this.foundOrganizationName == null || this.foundOrganizationName == "") &&
			(this.foundRicsOrganizationId == null || this.foundRicsOrganizationId == "") &&
			(this.foundRbdmsOwnerNumber == null || this.foundRbdmsOwnerNumber == "");
		return value;
	}

	get organizationSearched(): boolean {
		if (this.form == null || this.form.get('organizationSearched') == null) return false;
		let value = <boolean>this.form.get('organizationSearched').value;
		return value;
	}

	set organizationSearched(value: boolean) {
		if (this.form == null || this.form.get('organizationSearched') == null) {
			console.log("can't find searched control to set value");
			return;
		}
		this.form.get('organizationSearched').setValue(value);
	}

    get ownerNameMismatch(): string | null {
        if (this.form == null || this.form.get('inputForm') == null || this.form.get('inputForm').get('ownerNumber') == null) return null;
        let ownerName: string = this.form.get('inputForm').get('ownerName').value;
        let ownerNumber: string = this.form.get('inputForm').get('ownerNumber').value;
        if (ownerName == null || ownerName.length === 0 || ownerNumber == null || ownerNumber.length === 0) return null;
        let owner = (ownerNumber.toLocaleLowerCase().startsWith('on'))
            ? this.availableOrganizations.find(o => o.id.toLocaleLowerCase() === ownerNumber.toLocaleLowerCase())
            : this.availableOrganizations.find(o => o.historicalOwnerNumber.toLocaleLowerCase() === ownerNumber.toLocaleLowerCase());
        if (owner == null || owner.name.toLocaleLowerCase() === ownerName.toLocaleLowerCase()) return null;
        return "Owner name on file for organization does not match the provided owner name.";
    }

    get ownerNumberNotOnFile(): string | null {
        if (this.form == null || this.form.get('inputForm') == null || this.form.get('inputForm').get('ownerNumber') == null) return null;
        let ownerNumber = this.form.get('inputForm').get("ownerNumber").value;
        if (ownerNumber == null || ownerNumber.length === 0) return null;
        if (ownerNumber.length === 1 && ownerNumber.toLocaleUpperCase() === 'O') return null;
        if (ownerNumber.length >= 2 && ownerNumber.length < 15 && ownerNumber.toLocaleUpperCase().startsWith('ON')) return null;
        let index = this.availableOwnerNumbers.findIndex(o => o === ownerNumber.toLocaleUpperCase());
        if (index !== -1) return null;
        return "Owner number not on file.";
    }

    get pendingAPIToReserve(): string | null {
        if (this.form == null || this.form.get('inputForm') == null || this.form.get('inputForm').get('holeNumber') == null) return null;
        let value = this.form.get('inputForm').get('holeNumber').value;
        if (value == null || value == "") return null;
        let hole = <number>value;
        const countyNumber = this.county.apiNumber.toString().padStart(3, "0");
        return `34${countyNumber}${hole}0000`;
    }

    pointTypes = GISPointTypes;

    get spuddedDate(): Date | null {
        if (this.form == null || this.form.get('inputForm') == null || this.form.get('inputForm').get('spudDate') == null) return null;
        let date = this.form.get('inputForm').get('spudDate').value
        return date != null ? new Date(date) : null;
    }

    private subheader: SubHeader = new SubHeader({ title: "Reserve API Numbers" });
    private subscription: Subscription = new Subscription();

    get totalDepthDate(): Date | null {
        if (this.form == null || this.form.get('inputForm') == null || this.form.get('inputForm').get('totalDepthDate') == null) return null;
        let date = this.form.get('inputForm').get('totalDepthDate').value
        return date != null ? new Date(date) : null;
    }

    successMessage: string = "API #[XXX] has been successfully reserved.  Issue the related permit outside of RICS as soon as possible.  Do you wish to reserve another?"
    successMessgeOrphan: string = "Historic or Orphan well with API #[XXX] has been successfully created.  Do you wish to submit another?";
    successDialog: Dialog = new Dialog("", [new DialogButton("No", false, () => { this.exit(); }), new DialogButton("Yes", true, () => { this.reset(); })]);
    failDialogMessage: string = "[DESCRIPTION] API #[XXX] was unsuccessful.";
    failDialog: Dialog = new Dialog("", [new DialogButton("OK", false, () => { return false; })]);
    failDialogOrphan: Dialog = new Dialog("", [new DialogButton("OK", false, () => { return false; })]);

    townshipList: string[] = [];

    updatedByGISMessage: string = "The following values were updated based on the results of querying GIS: [XXX]";
    updatedByGISDialog: Dialog = new Dialog("", [new DialogButton("OK", false, () => { return false; })]);

    wellStatus: Domain[] = [];
    wellStatusReason: Domain[] = [];
    wellDisposition: Domain[] = [];
    wellTypes: Domain[] = [];
	wellTypeDescription: Domain[] = [];
	availableWellTypeDescriptions: Option[] = [];

    constructor(private subheaderService: SubHeaderService, private svc: WellService, private dialogService: DynamicDialogService, private organizationService: OrganizationService,
        private builder: FormBuilder, private loader: LoaderService, private router: Router, private noteService: NoteService, private docService: DocumentManagementService,
        private casePipe: TitleCasePipe, private domainService: DomainDefinitionService, private queryService: GisQueryService) { }

    ngOnInit() {
        this.subheaderService.updateSubHeader(this.subheader);
		this.loadContent();
    }

    ngOnDestroy() {
        if (this.subscription != null) this.subscription.unsubscribe();
    }

    addNote(evt: Note): void {
        if (this.notes == null) this.notes = [];
        this.notes.push(evt);
    }

    backToInputs(): void {
        this.form.get('confirmData').setValue(false);
        this.form.get('inputData').setValue(true);
        this.form.get('dataIsCorrect').setValue(false);
        this.form.get('dataIsCorrect').clearValidators();
        this.form.get('dataIsCorrect').updateValueAndValidity();
        this.form.get('inputForm').enable();
    }

    backToSelection(): void {
        this.form.removeControl("inputForm");
		this.form.reset();
		this.form.updateValueAndValidity();
    }

    private buildForm(): FormGroup {
        return this.builder.group({
            UIC: ["", [Validators.required]],
            historic: ["", []],
            county: ["", [Validators.required]],
            holeNumber: ["", [Validators.required]],
            inputData: [false, []],
            confirmData: [false, []],
            dataIsCorrect: [false, []],
			gisValidated: [false, []],
			organizationName: [null, []],
			ricsOrganizationId: [null, []],
			rbdmsOwnerNumber: [null, []],
			organizationConfirmed: [{ value: false, disabled: true }, []],
			organizationSearched: [false, []],
			foundOrganizationName: [null, []],
			foundRicsOrganizationId: [null, []],
			foundRbdmsOwnerNumber: [null, []],
			wellType: ["", []],
			wellTypeDescription: [{ value: "", disabled: true}, []]
        });
    }

	private buildWellInputForm(hole: number): FormGroup {
		return this.builder.group({
			holeNumber: [hole, [Validators.required]],
			aNumber: ["", []],
			pepperNumber: ["", []],
			ownerName: [{ value: "", disabled: false }, [Validators.required]],
			ownerNumber: [{ value: "", disabled: false }, [Validators.required]],
			rbdmsNumber: [{ value: "", disabled: false}, []],
            wellName: ["", [Validators.required]],
            wellNumber: ["", [Validators.required]],
            civilTownship: ["", [Validators.required]],
            county: [{ value: this.county, disabled: true}, []],
            slant: ["", [Validators.required]],
            section: ["", []],
            lot: ["", []],
            tract: ["", []],
            allotment: ["", []],
            originalSurvey: ["", []],
            fraction: ["", []],
            surfaceLongitude: ["", [Validators.required, Validators.max(-0.000001)]],
            surfaceLatitude: ["", [Validators.required, Validators.min(0.000001)]],
            lastTakePointLongitude: ["", []],
            lastTakePointLatitude: ["", []],
            lastTakeCounty: [{ value: null, disabled: true }, []],
            lastTakeTownship: ["", []],
            lastTakeSection: ["", []],
            lastTakeLot: ["", []],
            lastTakeTract: ["", []],
            firstTakePointLongitude: ["", []],
            firstTakePointLatitude: ["", []],
            firstTakeCounty: [{ value: null, disabled: true }, []],
            firstTakeTownship: ["", []],
            firstTakeSection: ["", []],
            firstTakeLot: ["", []],
            firstTakeTract: ["", []],
            targetLatitude: ["", []],
            targetLongitude: ["", []],
            targetCounty: [{ value: null, disabled: true }, []],
            targetTownship: ["", []],
            targetSection: ["", []],
            targetLot: ["", []],
            targetTract: ["", []],
            wellStatus: ["", [Validators.required]],
            wellDisposition: ["", [Validators.required]],
            statusReason: ["", []],
			wellType: [{ value: "", disabled: false }, []],
			wellTypeDescription: [{ value: "", disabled: false }, []],
            spudDate: [null, []],
            totalDepthDate: [null, []],
            notes: [[], []],
            countyVerified: [null, [Validators.requiredTrue]],
            firstTakeVerified: [null, []],
            lastTakeVerified: [null, []],
            targetVerified: [null, []]
        });
    }

    cancelGenerate(): void {
        this.dialogDecline.toggleDialog(true);
        this.dialogService.updateDialog(this.dialogDecline);
    }

    confirmGenerate(): void {
        let dialog: Dialog = null;
        if (this.formattedAPI == this.nextAPIForCounty) {
            this.dialogConfirmMatch.message = this.dialogMatchText.replace("[XXX]", this.formattedAPI);
            dialog = this.dialogConfirmMatch;
        } else {
            this.dialogConfirmMismatch.message = this.dialogMismatchText.replace("[XXX]", this.formattedAPI);
            this.dialogConfirmMismatch.message = this.dialogConfirmMismatch.message.replace("[YYY]", this.nextAPIForCounty);
            dialog = this.dialogConfirmMismatch;
        }
        if (dialog != null) {
            dialog.toggleDialog(true);
            this.dialogService.updateDialog(dialog);
        }
    }

    confirmInputs(): void {
        this.form.get('confirmData').setValue(true);
        this.form.get('inputData').setValue(false);
        this.form.get('dataIsCorrect').setValidators([Validators.requiredTrue]);
        this.form.get('inputForm').disable();
    }

    confirmOrphanAPI(): void {
        let dialog: Dialog = null;
        if (this.formattedAPI == this.nextAPIForCounty) {
            this.dialogOrphanConfirmMatch.message = this.dialogMatchText.replace("[XXX]", this.formattedAPI);
            dialog = this.dialogOrphanConfirmMatch;
        } else {
            this.dialogOrphanConfirmMismatch.message = this.dialogMismatchText.replace("[XXX]", this.formattedAPI);
            this.dialogOrphanConfirmMismatch.message = this.dialogOrphanConfirmMismatch.message.replace("[YYY]", this.nextAPIForCounty);
            dialog = this.dialogOrphanConfirmMismatch;
        }
        if (dialog != null) {
            dialog.toggleDialog(true);
            this.dialogService.updateDialog(dialog);
        }
    }

    deleteNote(idx: number): void {
        if (idx !== -1) {
            this.notes.splice(idx, 1);
        }
    }

    exit(): void {
        this.router.navigate(["/home"]);
    }

    private generateAPI(provided: boolean): void {
        let holeToUse: number;
        if (provided) {
            holeToUse = this.holeNumber;
        } else {
            let apiPart = this.apiList.find(a => a.county.apiNumber == this.county.apiNumber);
            if (apiPart == null) return;
            holeToUse = (this.isUIC) ? apiPart.nextAvailableCurrentHole : apiPart.nextAvailableHistoricHole;
        }
        const apiComponents: ApiComponents = {
            state: 34,
            county: this.county.apiNumber,
            holeNumber: holeToUse,
            sideTrack: 0,
            completionNumber: 0
        };
        const request: ReserveAPIRequest = {
            apiComponents: apiComponents,
			county: this.county,
			ricsOrganizationId: this.form.get('foundRicsOrganizationId').value,
			rbdmsOwnerNumber: this.form.get('foundRbdmsOwnerNumber').value,
			organizationName: this.form.get('foundOrganizationName').value,
			wellType: this.form.get('wellType').value,
			wellTypeDescription: this.form.get('wellTypeDescription').value
        };
        let obs$ = this.svc.reserveAPINumber(request).pipe(
            finalize(() => { this.loader.toggleLoader(false); })
        );
        let apiNumber = (provided) ? this.formattedAPI : this.nextAPIForCounty;
        this.loader.toggleLoader(true);
        let subs$ = obs$.subscribe(
            (result: boolean) => {
                if (result) {                    
                    this.successDialog.message = this.successMessage.replace("[XXX]", apiNumber);
                    this.successDialog.toggleDialog(true);
                    this.dialogService.updateDialog(this.successDialog);
                } else {
                    this.failDialog.message = this.failDialogMessage.replace("[DESCRIPTION]", "Reservation of");
                    this.failDialog.message = this.failDialog.message.replace("[XXX]", apiNumber);
                    this.failDialog.toggleDialog(true);
                    this.dialogService.updateDialog(this.failDialog);
                }
            },
            (error) => {
                this.logError(error);
            }
        );
        this.subscription.add(subs$);
    }

    generateProvided(): void {
        this.generateAPI(true);
    }

    generateRecommended(): void {
        this.generateAPI(false);
    }

    getData(): Observable<any> {
        let obs$: Observable<any>[] = [];

        let holeNumbers$ = this.svc.getReservedHoleNumbers().pipe(
            tap((data: ReservedHoleNumbers[]) => {
                this.apiList = data;
            }),
            catchError((error) => {
                this.logError(error);
                return of([]);
            })
        );
		obs$.push(holeNumbers$);

		let organizations$: Observable<OrganizationStub[]> =
			this.availableOrganizations != null && this.availableOrganizations.length > 0
				? of(this.availableOrganizations)
				: this.organizationService.getDataForNameValidation().pipe(
					tap((organizations: OrganizationStub[]) => {
						this.availableOrganizations = organizations;
						let ids = organizations.map(o => o.id);
						let rbdmsIds = organizations.filter(o => o.historicalOwnerNumber != null && o.historicalOwnerNumber.length > 0).map(o => o.historicalOwnerNumber);
						ids.forEach(i => { this.availableOwnerNumbers.push(i); });
						rbdmsIds.forEach(i => { this.availableOwnerNumbers.push(i); })
					}),
					catchError((error) => {
						this.logError(error);
						return of([]);
					}));
        
		obs$.push(organizations$);

		let township$: Observable<any[] | string[]> =
			this.townshipList != null && this.townshipList.length > 0
				? of(this.townshipList)
				: this.docService.findKeywordMappings({ keywordName: "DOGRM - Township" } as OnBaseKeywordMappingSearchSpec)
					.pipe(
						map((keys: OnBaseMappedKeyword[]) => {
							let options: string[] = [];
							keys.forEach(k => { k.dropdownValues.forEach(v => { options.push(this.casePipe.transform(v.value)); }); });
							options = options.filter(this.onlyUnique);
							return options;
						}),
						tap((townships: string[]) => {
							this.townshipList = townships;
							this.townshipList.unshift("");
						}),
						catchError((error) => {
							this.logError(error);
							return of([]);
						})
					);

		obs$.push(township$);

		let wellStatus$: Observable<any[] | Domain[]> =
			this.wellStatus != null && this.wellStatus.length > 0
				? of(this.wellStatus)
				: this.domainService.getValues("WL_STATUS").pipe(
					tap((domains: Domain[]) => {
						this.wellStatus = domains.sort((a, b) => {
							if (a.Definition > b.Definition) return 1;
							if (a.Definition < b.Definition) return -1;
							return 0;
						});;
					}),
					catchError((error) => {
						this.logError(error);
						return of([]);
					})
				);

		obs$.push(wellStatus$);

		let disposition$: Observable<any[] | Domain[]> =
			this.wellDisposition != null && this.wellDisposition.length > 0
				? of(this.wellDisposition)
				: this.domainService.getValues("STATUS_DESC").pipe(
					tap((domains: Domain[]) => {
						this.wellDisposition = domains.sort((a, b) => {
							if (a.Definition > b.Definition) return 1;
							if (a.Definition < b.Definition) return -1;
							return 0;
						});;
					}),
					catchError((error) => {
						this.logError(error);
						return of([]);
					})
				);

		obs$.push(disposition$);

		let statusReason$: Observable<any[] | Domain[]> = 
			this.wellStatusReason != null && this.wellStatusReason.length > 0
				? of(this.wellStatusReason)
				: this.domainService.getValues("STATUS_REAS").pipe(
					tap((domains: Domain[]) => {
						this.wellStatusReason = domains.sort((a, b) => {
							if (a.Definition > b.Definition) return 1;
							if (a.Definition < b.Definition) return -1;
							return 0;
						});;
					}),
					catchError((error) => {
						this.logError(error);
						return of([]);
					})
				);
        obs$.push(statusReason$);

        const typeDescription$ = this.domainService.getValues("WLTYP_DESC").pipe(
            tap((domains: Domain[]) => {
                this.wellTypeDescription = domains.sort((a, b) => {
                    if (a.Definition > b.Definition) return 1;
                    if (a.Definition < b.Definition) return -1;
                    return 0;
				});
            }),
            catchError((error) => {
                this.logError(error);
                return of([]);
            })
        );
		obs$.push(typeDescription$);

		

        const type$ = this.domainService.getValues("WELL_TYP").pipe(            
            catchError((error) => {
                this.logError(error);
                return of([]);
            })
        );
        const wlType$ = this.domainService.getValues("WELL_TYPE").pipe(
            catchError((error) => {
                this.logError(error);
                return of([]);
            })
        );
        const allTypes$: Observable<Domain[][]> = combineLatest(type$, wlType$).pipe(
            tap((domains: Domain[][]) => {
                domains.forEach(domain => {
                    domain.forEach(d => {
                        if (this.wellTypes.findIndex(w => w.Code === d.Code) === -1) {
                            this.wellTypes.push(d);
                        }
                    })
				});
				this.wellTypes.unshift({ Code: "", Definition: "--Select--" } as Domain);
                this.wellTypes = this.wellTypes.sort((a, b) => {
                    if (a.Definition > b.Definition) return 1;
                    if (a.Definition < b.Definition) return -1;
                    return 0;
                });
            })
		);
		if (this.wellTypes == null || this.wellTypes.length === 0) {
			obs$.push(allTypes$);
		}
		if (this._typeDescriptionOptions == null || this._typeDescriptionOptions.length === 0) {
			obs$.push(this.getWellTypeDescriptionOptions());
		}
        return forkJoin(obs$);
    }

	getWellTypeDescriptionOptions(): Observable<DialogOptionSet[]> {
		let oilgas$: Observable<DialogOptionSet> = this.domainService.getValues("OilAndGasWellTypeDescriptions").pipe(
			map((data: DomainValue[]) => {
				let set = new DialogOptionSet();
				set.value = "OG_R";
				set.set = [];
				data.forEach(d => {
					let option: Option = {
						id: d.Code,
						text: d.Definition
					};
					if (option.id.length === 0) option.id = " ";
					set.set.push(option);
				})
				return set;
			})
		);

		let recovery$: Observable<DialogOptionSet> = this.domainService.getValues("EnhancedRecoveryWellTypeDescriptions").pipe(
			map((data: DomainValue[]) => {
				let set = new DialogOptionSet();
				set.value = "ER_R";
				set.set = [];
				data.forEach(d => {
					let option: Option = {
						id: d.Code,
						text: d.Definition
					};
					if (option.id.length === 0) option.id = " ";
					set.set.push(option);
				})
				return set;
			})
		);

		let disposal$: Observable<DialogOptionSet> = this.domainService.getValues("DisposalWellTypeDescriptions").pipe(
			map((data: DomainValue[]) => {
				let set = new DialogOptionSet();
				set.value = "SW_R";
				set.set = [];
				data.forEach(d => {
					let option: Option = {
						id: d.Code,
						text: d.Definition
					};
					if (option.id.length === 0) option.id = " ";
					set.set.push(option);
				})
				return set;
			})
		);

		let unknown$: Observable<DialogOptionSet> = this.domainService.getValues("UnknownWellTypeDescriptions").pipe(
			map((data: DomainValue[]) => {
				let set = new DialogOptionSet();
				set.value = "UN_R";
				set.set = [];
				data.forEach(d => {
					let option: Option = {
						id: d.Code,
						text: d.Definition
					};
					if (option.id.length === 0) option.id = " ";
					set.set.push(option);
				})
				return set;
			})
		);

		return forkJoin([oilgas$, recovery$, disposal$, unknown$]).pipe(
			tap((sets: DialogOptionSet[]) => {
				this._typeDescriptionOptions = sets;				
			})
		);
	}

    historicChanged(value: string): void {
		if (this.isHistoric === false && value === 'no') {
			this.dialogOrphanDecline.toggleDialog(true);
			this.dialogService.updateDialog(this.dialogOrphanDecline);
		} else {
			this.uicChanged('no'); //make sure validation gets reset properly for historic values
		}
    }

    inputOrphanInformation(hole: number): void {
        if (this.form.contains("inputForm") == false) {
            this.form.addControl("inputForm", this.buildWellInputForm(hole));
        }
		this.form.get('inputData').setValue(true);
		this.form.get('inputForm').get("ownerName").setValue(this.form.get("foundOrganizationName").value);
		this.form.get('inputForm').get("ownerNumber").setValue(this.form.get("foundRicsOrganizationId").value);
		this.form.get('inputForm').get("wellType").setValue(this.form.get("wellType").value);
		this.form.get('inputForm').get("wellTypeDescription").setValue(this.form.get("wellTypeDescription").value);
    }

	loadContent(): void {
		this.dataLoading.next(true);
		let dataSubs$ = this.getData().pipe(
			finalize(() => { this.dataLoading.next(false); })
		).subscribe(
			() => { this.form = this.buildForm(); },
			(error) => {
				this.logError(error);
			}
		);
		this.subscription.add(dataSubs$);
		
	}

    logError(error: any): void {
        console.error(error);
        this.dialogService.showError();
    }

	private mapFormToWell(): Well {
        let inputForm = this.form.get('inputForm');
        let well = {
            id: `34${this.county.apiNumber.toString().padStart(3, "0")}${inputForm.get('holeNumber').value}0000`,
            ohioPeperNumber: inputForm.get('pepperNumber').value,
            organizationName: inputForm.get('ownerName').value,
			organizationId: inputForm.get('ownerNumber').value,
			historicalOwnerNumber: inputForm.get('rbdmsNumber').value,
            alternateNumbers: [],
            apiComponents: {
                state: 34,
                county: this.county.apiNumber,
                holeNumber: inputForm.get('holeNumber').value,
                completionNumber: 0,
                sideTrack: 0
            } as ApiComponents,
            newOrExisting: "new",
            lease: {
                name: inputForm.get('wellName').value
            } as Lease,
            name: `${inputForm.get('wellName').value} ${inputForm.get('wellNumber').value}`,
            wellNumber: inputForm.get('wellNumber').value,
			dateWellDrillingStarted: inputForm.get('spudDate').value,
			dateOfOriginalSpud: inputForm.get('spudDate').value,
            dateTotalDepthReached: inputForm.get('totalDepthDate').value,
            location: {
                confidence: null,
                dateModified: null,
                directionsToWell: null,
                elevation: null,
                isUrban: false,
                sourceOfLocation: null,
                slant: inputForm.get('slant').value,
                gisPoints: [
                    {
                        pointType: GISPointTypes.WellHead,
                        county: this.county,
                        lot: inputForm.get('lot').value,
                        section: inputForm.get('section').value,
                        township: {
                            name: inputForm.get('civilTownship').value
                        } as DirectionIdentifier,
                        allotment: inputForm.get('allotment').value,
                        originalSurvey: inputForm.get('originalSurvey').value,
                        tract: inputForm.get('tract').value,
                        fraction: inputForm.get('fraction').value,
                        coordinates: {
                            longitude83: inputForm.get('surfaceLongitude').value,
                            latitude83: inputForm.get('surfaceLatitude').value
                        } as Coordinates
                    } as GISPoint
                ]
            } as WellLocation,
            wellStatus: {
                disposition: inputForm.get('wellDisposition').value,
                legacyTracking: inputForm.get('statusReason').value,
                type: {
                    type: inputForm.get('wellType').value,
                    description: inputForm.get('wellTypeDescription').value
                } as WellType,
                permittingModifiers: 0                
            } as WellStatus
        } as Well;
        if (inputForm.get('aNumber').value != null && inputForm.get('aNumber').value.length > 0) {
            well.alternateNumbers.push(inputForm.get('aNumber').value);
        }
        if (well.location.slant != WellDirection.Vertical) {
            let target = {
                pointType: GISPointTypes.Target,
                coordinates: {
                    longitude83: inputForm.get('targetLongitude').value,
                    latitude83: inputForm.get('targetLatitude').value
                } as Coordinates,
                county: {
                    apiNumber: inputForm.get('targetCounty').value.apiNumber,
                    name: inputForm.get('targetCounty').value.name
                } as County,
                section: inputForm.get('targetSection').value,
                lot: inputForm.get('targetLot').value,
                tract: inputForm.get('targetTract').value,
                township: {
                    name: inputForm.get('targetTownship').value
                } as DirectionIdentifier
            } as GISPoint;
            well.location.gisPoints.push(target);
        }
        if (well.location.slant === WellDirection.OtherHorizontal || well.location.slant === WellDirection.Horizontal) {
            let firstTakePoint = {
                pointType: GISPointTypes.Toe,
                coordinates: {
                    longitude83: inputForm.get('firstTakePointLongitude').value,
                    latitude83: inputForm.get('firstTakePointLatitude').value
                } as Coordinates,
                county: {
                    apiNumber: inputForm.get('firstTakeCounty').value.apiNumber,
                    name: inputForm.get('firstTakeCounty').value.name
                } as County,
                section: inputForm.get('firstTakeSection').value,
                lot: inputForm.get('firstTakeLot').value,
                tract: inputForm.get('firstTakeTract').value,
                township: {
                    name: inputForm.get('firstTakeTownship').value
                } as DirectionIdentifier
            } as GISPoint;
            well.location.gisPoints.push(firstTakePoint);
            let lastTakePoint = {
                pointType: GISPointTypes.Heel,
                coordinates: {
                    longitude83: inputForm.get('lastTakePointLongitude').value,
                    latitude83: inputForm.get('lastTakePointLatitude').value
                } as Coordinates,
                county: {
                    apiNumber: inputForm.get('lastTakeCounty').value.apiNumber,
                    name: inputForm.get('lastTakeCounty').value.name
                } as County,
                section: inputForm.get('lastTakeSection').value,
                lot: inputForm.get('lastTakeLot').value,
                tract: inputForm.get('lastTakeTract').value,
                township: {
                    name: inputForm.get('lastTakeTownship').value
                } as DirectionIdentifier
            } as GISPoint;
            well.location.gisPoints.push(lastTakePoint);
        }
        return well;
    }

    private onlyUnique(value, index, self) {
        return self.indexOf(value) === index;
    }

    queryCounty(pointType: GISPointTypes): Observable<boolean> {
        let inputForm = this.form.get('inputForm');
        let latLong: number[];
        switch (pointType) {
            case GISPointTypes.WellHead:
                latLong = [Number(inputForm.get('surfaceLatitude').value), Number(inputForm.get('surfaceLongitude').value)]
                break;
            case GISPointTypes.Heel:
                latLong = [Number(inputForm.get('lastTakePointLatitude').value), Number(inputForm.get('lastTakePointLongitude').value)]
                break;
            case GISPointTypes.Toe:
                latLong = [Number(inputForm.get('firstTakePointLatitude').value), Number(inputForm.get('firstTakePointLongitude').value)]
                break;
            case GISPointTypes.Target:
                latLong = [Number(inputForm.get('targetLatitude').value), Number(inputForm.get('targetLongitude').value)]
                break;
        }
        let query = this.queryService.resolveQueryAtPoint(this.queryService.config.yearlyGISDataUrl, "Counties", latLong);
        return from(query).pipe(
            map((res: any) => {
                let verified: boolean = false;
                if (res != null && res.features != null && res.features.length > 0) {
                    let resolvedCountyName: string = res.features[0].attributes["COUNTY"] || "";
                    let resolvedCountyNumber: number = parseInt(res.features[0].attributes["FIPS"], 10);
                    if (pointType === GISPointTypes.WellHead) {
                        if (resolvedCountyName.trim().toLocaleLowerCase() != this.county.name.trim().toLocaleLowerCase()) {
                            inputForm.get('countyVerified').setValue(verified);
                            this.dialogCountyError.message = this.countyErrorMessage.replace("[COUNTY]", this.county.name);
                            this.dialogCountyError.message = this.dialogCountyError.message.replace("[GISCOUNTY]", resolvedCountyName);
                            this.dialogCountyError.toggleDialog(true);
                            this.dialogService.updateDialog(this.dialogCountyError);
                        } else {
                            verified = true;
                            inputForm.get('countyVerified').setValue(verified);                            
                        }
                    } else {
                        let county: County = {
                            apiNumber: resolvedCountyNumber,
                            name: resolvedCountyName
                        };
                        let apiItem = this.apiList.find(i => i.county.apiNumber === county.apiNumber);
                        verified = apiItem != null;
                        if (pointType === GISPointTypes.Toe) {
                            inputForm.get('firstTakeCounty').setValue(apiItem.county);
                            inputForm.get('firstTakeVerified').setValue(verified);
                        } else if (pointType === GISPointTypes.Heel) {
                            inputForm.get('lastTakeCounty').setValue(apiItem.county);
                            inputForm.get('lastTakeVerified').setValue(verified);
                        } else if (pointType === GISPointTypes.Target) {
                            inputForm.get('targetCounty').setValue(apiItem.county);
                            inputForm.get('targetVerified').setValue(verified);
                        }
                    }
                   
                }
                return verified;
            })
        ); 
    }

    querySectionLotTract(pointType: GISPointTypes): Observable<UpdatedGISValue[]> {
        let inputForm = this.form.get('inputForm');
        let latLong: number[];
        let originalSection: any = "";
        let originalLot: any = "";
        let originalTract: any = "";
        switch (pointType) {
            case GISPointTypes.WellHead:
                latLong = [Number(inputForm.get('surfaceLatitude').value), Number(inputForm.get('surfaceLongitude').value)];
                originalSection = inputForm.get('section').value;
                originalLot = inputForm.get('lot').value;
                originalTract = inputForm.get('tract').value;
                break;
            case GISPointTypes.Heel:
                latLong = [Number(inputForm.get('lastTakePointLatitude').value), Number(inputForm.get('lastTakePointLongitude').value)];
                originalSection = inputForm.get('lastTakeSection').value;
                originalLot = inputForm.get('lastTakeLot').value;
                originalTract = inputForm.get('lastTakeTract').value;
                break;
            case GISPointTypes.Toe:
                latLong = [Number(inputForm.get('firstTakePointLatitude').value), Number(inputForm.get('firstTakePointLongitude').value)];
                originalSection = inputForm.get('firstTakeSection').value;
                originalLot = inputForm.get('firstTakeLot').value;
                originalTract = inputForm.get('firstTakeTract').value;
                break;
            case GISPointTypes.Target:
                latLong = [Number(inputForm.get('targetLatitude').value), Number(inputForm.get('targetLongitude').value)];
                originalSection = inputForm.get('targetSection').value;
                originalLot = inputForm.get('targetLot').value;
                originalTract = inputForm.get('targetTract').value;
                break;
        }
        let query = this.queryService.resolveQueryAtPoint(this.queryService.config.yearlyGISDataUrl, "land_subdivisions_old", latLong);
        return from(query).pipe(
            map((result: any) => {
                let updates: UpdatedGISValue[] = [];
                let sectionChange: UpdatedGISValue = new UpdatedGISValue("Section", originalSection, "");
                let lotChange: UpdatedGISValue = new UpdatedGISValue("Lot", originalLot, "");
                let tractChange: UpdatedGISValue = new UpdatedGISValue("Tract", originalTract, "");
                if (result != null && result.features != null && result.features.length > 0) {
                    const data = result.features[0].attributes;
                    let sectionComponent: AbstractControl = null;
                    let lotComponent: AbstractControl = null;
                    let tractComponent: AbstractControl = null;
                    switch (pointType) {
                        case GISPointTypes.WellHead:
                            sectionComponent = inputForm.get('section');
                            lotComponent = inputForm.get('lot');
                            tractComponent = inputForm.get('tract');
                            break;
                        case GISPointTypes.Heel:
                            sectionComponent = inputForm.get('lastTakeSection');
                            lotComponent = inputForm.get('lastTakeLot');
                            tractComponent = inputForm.get('lastTakeTract');
                            break;
                        case GISPointTypes.Toe:
                            sectionComponent = inputForm.get('firstTakeSection');
                            lotComponent = inputForm.get('firstTakeLot');
                            tractComponent = inputForm.get('firstTakeTract');
                            break;
                        case GISPointTypes.Target:
                            sectionComponent = inputForm.get('targetSection');
                            lotComponent = inputForm.get('targetLot');
                            tractComponent = inputForm.get('targetTract');
                            break;
                    }
                    if (data["SECTION_"] != null) {
                        let section = data["SECTION_"].toString().trim().length > 0 ? data["SECTION_"] : "";
                        sectionChange.correctedValue = section;
                        if (section !== sectionChange.originalValue) {
                            
                            sectionComponent.setValue(section);
                        } 
                    }
                    if (data["LOT"] != null) {
                        let lot = data["LOT"].toString().trim().length > 0 ? data["LOT"] : "";
                        lotChange.correctedValue = lot;
                        if (lot !== lotChange.originalValue) {
                            
                            lotComponent.setValue(lot);
                        }
                    }
                    if (data["TRACT"] != null) {
                        let tract = data["TRACT"].toString().trim().length > 0 ? data["TRACT"] : "";
                        tractChange.correctedValue = tract;
                        if (tract !== tractChange.originalValue) {
                            
                            tractComponent.setValue(tract);
                        }
                    }
                }
                updates.push(sectionChange);
                updates.push(lotChange);
                updates.push(tractChange);
                return updates;
            }
            )
        );
    }

    queryTownship(pointType: GISPointTypes): Observable<UpdatedGISValue> {
        let inputForm = this.form.get('inputForm');
        let latLong: number[];
        let originalValue: any = "";
        switch (pointType) {
            case GISPointTypes.WellHead:
                latLong = [Number(inputForm.get('surfaceLatitude').value), Number(inputForm.get('surfaceLongitude').value)];
                originalValue = inputForm.get('civilTownship').value;
                break;
            case GISPointTypes.Heel:
                latLong = [Number(inputForm.get('lastTakePointLatitude').value), Number(inputForm.get('lastTakePointLongitude').value)];
                originalValue = inputForm.get('lastTakeTownship').value;
                break;
            case GISPointTypes.Toe:
                latLong = [Number(inputForm.get('firstTakePointLatitude').value), Number(inputForm.get('firstTakePointLongitude').value)];
                originalValue = inputForm.get('firstTakeTownship').value;
                break;
            case GISPointTypes.Target:
                latLong = [Number(inputForm.get('targetLatitude').value), Number(inputForm.get('targetLongitude').value)];
                originalValue = inputForm.get('targetTownship').value;
                break;
        }
        let query = this.queryService.resolveQueryAtPoint(this.queryService.config.yearlyGISDataUrl, "Townships", latLong, true);
        return from(query).pipe(
            map((res: any) => {
                let result: UpdatedGISValue = new UpdatedGISValue("Civil Township", originalValue, "");
                if (res != null && res.features != null && res.features.length > 0) {
                    let component: AbstractControl = null;
                    switch (pointType) {
                        case GISPointTypes.WellHead:
                            component = inputForm.get('civilTownship');
                            break;
                        case GISPointTypes.Heel:
                            component = inputForm.get('lastTakeTownship');
                            break;
                        case GISPointTypes.Toe:
                            component = inputForm.get('firstTakeTownship');
                            break;
                        case GISPointTypes.Target:
                            component = inputForm.get('targetTownship');
                            break;
					}
                    let resolvedTownshipName: string = res.features[0].attributes["TOWNSHIP"];
                    if (resolvedTownshipName.toLocaleUpperCase() === resolvedTownshipName) {
                        resolvedTownshipName = this.casePipe.transform(resolvedTownshipName);
                    }
                    result.correctedValue = resolvedTownshipName;
                    component.setValue(resolvedTownshipName);
                }
                return result;
            })
        )
    }


    reset(): void {
        this.form.reset();
        this.form.get('UIC').setValue("");
        this.form.get('historic').setValue("");
		this.form.removeControl('inputForm');
		this.loadContent();
	}

	searchDifferentOrganization(): void {
		this.organizationSearched = false;
		let nameCtrl = this.form.get('organizationName');
		let ricsIdCtrl = this.form.get('ricsOrganizationId');
		let rbdmsOwnerCtrl = this.form.get('rbdmsOwnerNumber');
		nameCtrl.reset();
		ricsIdCtrl.reset();
		rbdmsOwnerCtrl.reset();
		let confirmCtrl = this.form.get('organizationConfirmed');
		confirmCtrl.setValue(false);
		confirmCtrl.enable();
		this.foundOrganizationName = null;
		this.foundRicsOrganizationId = null;
		this.foundRbdmsOwnerNumber = null;
	}

    slantChanged(): void {
        let slant = this.form.get('inputForm').get('slant');
        let firstTakePointLatitudeCtrl = this.form.get('inputForm').get('firstTakePointLatitude');
        let firstTakePointLongitudeCtrl = this.form.get('inputForm').get('firstTakePointLongitude');
        let firstTakeVerifiedCtrl = this.form.get('inputForm').get('firstTakeVerified');
        let lastTakePointLatitudeCtrl = this.form.get('inputForm').get('lastTakePointLatitude');
        let lastTakePointLongitudeCtrl = this.form.get('inputForm').get('lastTakePointLongitude');
        let lastTakeVerifiedCtrl = this.form.get('inputForm').get('lastTakeVerified');
        let targetLatitudeCtrl = this.form.get('inputForm').get('targetLatitude');
        let targetLongitudeCtrl = this.form.get('inputForm').get('targetLongitude');
        let targetVerifiedCtrl = this.form.get('inputForm').get('targetVerified');
        if (slant.value === WellDirection.Directional) {
            targetLatitudeCtrl.setValidators([Validators.required, Validators.min(0.000001)]);
            targetLongitudeCtrl.setValidators([Validators.required, Validators.max(-0.000001)]);
            targetVerifiedCtrl.setValidators([Validators.requiredTrue]);
            firstTakePointLatitudeCtrl.clearValidators();
            firstTakePointLongitudeCtrl.clearValidators();
            firstTakeVerifiedCtrl.clearValidators();
            lastTakePointLatitudeCtrl.clearValidators();
            lastTakePointLongitudeCtrl.clearValidators();
            lastTakeVerifiedCtrl.clearValidators();            
        } else if (slant.value === WellDirection.Horizontal || slant.value === WellDirection.OtherHorizontal) {
            targetLatitudeCtrl.setValidators([Validators.required, Validators.min(0.000001)]);
            targetLongitudeCtrl.setValidators([Validators.required, Validators.max(-0.000001)]);
            targetVerifiedCtrl.setValidators([Validators.requiredTrue]);
            firstTakePointLatitudeCtrl.setValidators([Validators.required, Validators.min(0.000001)]);
            firstTakePointLongitudeCtrl.setValidators([Validators.required, Validators.max(-0.000001)]);
            firstTakeVerifiedCtrl.setValidators([Validators.requiredTrue]);
            lastTakePointLatitudeCtrl.setValidators([Validators.required, Validators.min(0.000001)]);
            lastTakePointLongitudeCtrl.setValidators([Validators.required, Validators.max(-0.000001)]);
            lastTakeVerifiedCtrl.setValidators([Validators.requiredTrue]);
        } else {
            targetLatitudeCtrl.clearValidators();;
            targetLongitudeCtrl.clearValidators();
            targetVerifiedCtrl.clearValidators();
            firstTakePointLatitudeCtrl.clearValidators();
            firstTakePointLongitudeCtrl.clearValidators();
            firstTakeVerifiedCtrl.clearValidators();
            lastTakePointLatitudeCtrl.clearValidators();
            lastTakePointLongitudeCtrl.clearValidators();
            lastTakeVerifiedCtrl.clearValidators();
        }
        targetLatitudeCtrl.updateValueAndValidity();;
        targetLongitudeCtrl.updateValueAndValidity();
        targetVerifiedCtrl.updateValueAndValidity();
        firstTakePointLatitudeCtrl.updateValueAndValidity();
        firstTakePointLongitudeCtrl.updateValueAndValidity();
        firstTakeVerifiedCtrl.updateValueAndValidity();
        lastTakePointLatitudeCtrl.updateValueAndValidity();
        lastTakePointLongitudeCtrl.updateValueAndValidity();
        lastTakeVerifiedCtrl.updateValueAndValidity();
        this.form.updateValueAndValidity();
    }

    spuddedDateChanged(evt: Date): void {
        let ctrl = this.form.get('inputForm').get('spudDate');
        if (ctrl != null) ctrl.setValue(evt);
    }

    startOrphanProvided(): void {
        this.inputOrphanInformation(this.holeNumber);
    }

    startOrphanRecommended(): void {
        let apiItem = this.apiList.find(a => a.county.apiNumber == this.county.apiNumber);
        if (apiItem == null) return;
        this.inputOrphanInformation(apiItem.nextAvailableHistoricHole);
    }

    submitOrphanWell(): void {
        let well: Well = this.mapFormToWell();
        let request: OrphanWellRequest = {
            requestedOrphanWell: well,
            submitWellToGIS: true
        } as OrphanWellRequest;

        let obs$ = this.svc.saveOrphanWell(request).pipe(
            switchMap((success: boolean) => {
                let retObs$: Observable<boolean> = of(success);
                if (success && this.notes.length > 0) {
                    let noteObs$: Observable<Note>[] = [];
					this.notes.forEach(n => {
						n.relatedItemId = well.id;
                        noteObs$.push(this.noteService.postNote(n))
                    });
                    retObs$ = forkJoin(noteObs$).pipe(
                        catchError((err) => {
                            console.error("error saving notes");
                            console.error(err);
                            return of([]);
                        }),
                        map((noteResults: Note[]) => {
                            return success; //don't block the reservation just because the notes failed, because the reservation already succeeded by this point
                        }))
                }
                return retObs$;
            }),
            catchError((err) => {
                console.error("error in well save");
                console.error(err);
                return of(false);
            }),
            finalize(() => { this.loader.toggleLoader(false); })
        );
        let apiNumber: string = well.id;
        this.loader.toggleLoader(true);
        let subs$ = obs$.subscribe(
            (result: boolean) => {
                if (result) {                    
                    this.successDialog.message = this.successMessgeOrphan.replace("[XXX]", apiNumber);
                    this.successDialog.toggleDialog(true);
                    this.dialogService.updateDialog(this.successDialog);
                } else {
                    this.failDialogOrphan.message = this.failDialogMessage.replace("[DESCRIPTION]", "Creation of orphan/historic well for");
                    this.failDialogOrphan.message = this.failDialogOrphan.message.replace("[XXX]", apiNumber);
                    this.failDialogOrphan.toggleDialog(true);
                    this.dialogService.updateDialog(this.failDialogOrphan);
                }
            },
            (error) => {
                this.logError(error);
            }
        );
        this.subscription.add(subs$);
    }

    totalDepthDateChanged(evt: Date): void {
        let ctrl = this.form.get('inputForm').get('totalDepthDate');
        if (ctrl != null) ctrl.setValue(evt);
    }

	uicChanged(value: string): void {
        if (this.isUIC) {
			this.form.get('historic').clearValidators();
			this.form.get('holeNumber').clearValidators();
			this.form.get('holeNumber').setValidators([Validators.required, Validators.min(this.currentHoleMinimum), Validators.max(this.currentHoleMaximum)]);
			this.form.get('wellType').setValidators([Validators.required]);
			this.form.get('wellTypeDescription').reset();
			this.form.get('wellTypeDescription').disable();
			this.form.get('organizationConfirmed').setValidators([Validators.requiredTrue]);
			this.form.get('organizationSearched').setValidators([Validators.requiredTrue]);
			this.form.get('foundOrganizationName').setValidators([Validators.required]);
			this.form.get('foundRicsOrganizationId').setValidators([Validators.required]);			
        } else if (this.isHistoric) {
			this.form.get('historic').setValidators([Validators.required]);
			this.form.get('holeNumber').clearValidators();
			this.form.get('holeNumber').setValidators([Validators.required, Validators.min(this.historicHoleMinimum), Validators.max(this.historicHoleMaximum)]);
			this.form.get('wellType').clearValidators();

			this.form.get('organizationConfirmed').clearValidators();
			this.form.get('organizationSearched').clearValidators();
			this.form.get('foundOrganizationName').clearValidators();
			this.form.get('foundRicsOrganizationId').clearValidators();
		} 
		this.form.get('foundRicsOrganizationId').setValue(null);
		this.form.get('foundRicsOrganizationId').updateValueAndValidity();
		this.form.get('organizationSearched').setValue(false);
		this.form.get('organizationSearched').updateValueAndValidity();
		this.form.get('organizationConfirmed').setValue(false);
		this.form.get('organizationConfirmed').updateValueAndValidity();
		this.form.get('wellType').reset();
		this.form.get('wellTypeDescription').reset();
		this.form.get('foundRicsOrganizationId').updateValueAndValidity();
		this.form.get('organizationConfirmed').updateValueAndValidity();
		
		this.form.get('foundOrganizationName').setValue(null);
		this.form.get('foundOrganizationName').updateValueAndValidity();
		this.form.get('wellType').updateValueAndValidity();
		this.form.get('historic').updateValueAndValidity();
		this.form.get('holeNumber').reset();
		this.form.get('holeNumber').updateValueAndValidity();
		this.form.get('wellTypeDescription').updateValueAndValidity();
        this.form.updateValueAndValidity();
	}

	updateAvailableDescriptions(evt: any): void {
		console.log('update called');
		console.log(evt);
		let wellType = this.form.get('wellType');
		let descriptionCtrl = this.form.get('wellTypeDescription');
		if (wellType != null && descriptionCtrl != null) {			
			if (wellType.value == null || wellType.value == "") {
				descriptionCtrl.setValue(null);
				this.availableWellTypeDescriptions = [];
				descriptionCtrl.updateValueAndValidity();
				descriptionCtrl.disable();
			} else {
				descriptionCtrl.setValue(null);
				if (descriptionCtrl.disabled) {
					descriptionCtrl.enable();
				}
				this.availableWellTypeDescriptions = [];				
				let applicableSets = this._typeDescriptionOptions.filter(opt => opt.value === evt);
				applicableSets.forEach(s => {
					if (s.set != null && s.set.length > 0) {
						s.set.forEach(set => {
							if (this.availableWellTypeDescriptions.findIndex(desc => desc.id === set.id) === -1) {
								this.availableWellTypeDescriptions.push(set);
							}
						})
					}
				});
				if (this.availableWellTypeDescriptions.length > 0) {
					descriptionCtrl.setValue(this.availableWellTypeDescriptions[0].id);
				}
				descriptionCtrl.updateValueAndValidity();
			}
		}
	}

    validateCoordinates(pointType: GISPointTypes): void {
        if (this.queryService.tokenService.userToken.value === "") {
            this.dialogNoGIS.toggleDialog(true);
            this.dialogService.updateDialog(this.dialogNoGIS);
        } else {
            let query: Observable<any> = this.queryCounty(pointType).pipe(
                switchMap((res: boolean) => {
                    if (res === true) {
                        return this.queryTownship(pointType);
                    } else {
                        return of(null);
                    }
                }),
                switchMap((value: UpdatedGISValue) => {
                    if (value != null) {
                        return this.querySectionLotTract(pointType).pipe(
                            map((updates: UpdatedGISValue[]) => {
                                updates.push(value);
                                return updates;
                            })
                        )
                    } else {
                        return of(null);
                    }
                })
            );

            this.loader.toggleLoader(true);
            let query$ = query.pipe(
                catchError((err) => {
                    console.error(err);
                    this.dialogNoGIS.toggleDialog(true);
                    this.dialogService.updateDialog(this.dialogNoGIS);
                    return of(null);
                }),
                finalize(() => { this.loader.toggleLoader(false); })
            ).subscribe(
                (updates: UpdatedGISValue[]) => {
                    if (updates != null) {
                        let changed = updates.filter(u => u.isCorrected);
                        if (changed.length > 0) {
                            let updateMessage: string = "";
                            changed.forEach(c => {
                                updateMessage += `<br />${c.toString()}`;
                            });
                            this.updatedByGISDialog.message = this.updatedByGISMessage.replace('[XXX]', updateMessage);
                            this.updatedByGISDialog.toggleDialog(true);
                            this.dialogService.updateDialog(this.updatedByGISDialog);
                        }
                    }
                },
                (error) => {
                    this.logError(error);
                }
            );
            this.subscription.add(query$);
        }
        
    }

	findOrganization(name: string, ricsId: string, rbdmsOwner: string): OrganizationStub | undefined {
		return this.availableOrganizations.find(o => (name == null || name === "" || o.name.toLocaleLowerCase() == name.toLocaleLowerCase()) && (ricsId == null || ricsId === "" || o.id == ricsId) && (rbdmsOwner == null || rbdmsOwner === "" || o.historicalOwnerNumber == rbdmsOwner));
	}

	validateOrphanOwner(evt: string): void {
		let inputForm = this.form.get('inputForm');
		if (inputForm != null) {
			let nameCtrl = inputForm.get('ownerName');
			let idCtrl = inputForm.get('ownerNumber');
			let rbdmsCtrl = inputForm.get('rbdmsNumber');
			if (nameCtrl == null || idCtrl == null || rbdmsCtrl == null) return;
			rbdmsCtrl.reset();
			let found: OrganizationStub = null;
			if (evt === 'id' && idCtrl.value != null && idCtrl.value != '') {
				nameCtrl.reset();
				found = this.findOrganization(null, idCtrl.value, null);
			} else if (evt === 'name' && nameCtrl.value != null && nameCtrl.value !== '') {
				idCtrl.reset();
				found = this.findOrganization(nameCtrl.value, null, null);
			}
			if (found != null) {
				idCtrl.setValue(found.id);
				rbdmsCtrl.setValue(found.historicalOwnerNumber);
				nameCtrl.setValue(found.name);
			}
		}
	}


	validateOrganization(): void {
		if (this.form != null) {
			let nameCtrl = this.form.get('organizationName');
			let ricsIdCtrl = this.form.get('ricsOrganizationId');
			let rbdmsOwnerCtrl = this.form.get('rbdmsOwnerNumber');
			if ((nameCtrl == null || nameCtrl.value == null || nameCtrl.value == null) &&
				(ricsIdCtrl == null || ricsIdCtrl.value == null || ricsIdCtrl.value == null) &&
				(rbdmsOwnerCtrl == null || rbdmsOwnerCtrl.value == null || rbdmsOwnerCtrl.value == null)) {
				this.dialogNoSelection.toggleDialog(true);
				this.dialogService.updateDialog(this.dialogNoSelection);
			} else {
				this.organizationSearched = true;
				let name = nameCtrl.value;
				let ricsId = ricsIdCtrl.value;
				let rbdmsOwner = rbdmsOwnerCtrl.value;
				let match = this.findOrganization(name, ricsId, rbdmsOwner);
				
				if (match != null) {
					this.foundOrganizationName = match.name;
					this.foundRicsOrganizationId = match.id;
					this.foundRbdmsOwnerNumber = match.historicalOwnerNumber;					
				}
			}
			let confirmCtrl = this.form.get('organizationConfirmed');
			if (confirmCtrl != null) {
				if (this.organizationNotFound) {
					confirmCtrl.setValue(false);
					confirmCtrl.updateValueAndValidity();
					confirmCtrl.disable();
				} else {					
					confirmCtrl.enable();
					confirmCtrl.updateValueAndValidity();
				}
			}
			
		}
	}
}


